import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'astroturf/react';

import * as ApiUtils from 'js/utils/api_utils';
import useExportFunnelTracking from 'js/premium_data_export/useExportFunnelTracking';
import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { Mono } from 'js/design_system/Mono';
import { Button } from 'js/design_system/Button';
import { TextButton } from 'js/design_system/TextButton';
import { Icon } from 'js/design_system/Icon';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import { Modal, ModalContent, ModalButtonGroup } from 'js/design_system/Modal';
import Divider from 'js/design_system/Divider';

// ─── Styles ──────────────────────────────────────────────────────────────────

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  margin-bottom: var(--spacing-xs-8, 8px);
`;

const BreadcrumbText = styled(Mono).attrs({ size: 'sm' })`
  text-transform: uppercase;
  color: var(--text-secondary, #535366);
  letter-spacing: 0.04em;
`;

const BreadcrumbSep = styled(Mono).attrs({ size: 'sm' })`
  color: var(--text-secondary, #535366);
`;

const ProgressTrack = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: var(--spacing-sm-16, 16px);
`;

const ProgressSegment = styled.div<{ filled: boolean }>`
  height: 2px;
  flex: 1;
  border-radius: 2px;
  background: var(--stroke-light, #dddde2);

  &.filled {
    background: var(--brand-colors-primary, #0645b1);
  }
`;

const ModalTitle = styled(Heading).attrs({
  fontFamily: 'serif',
  size: 'md',
  as: 'h2',
})`
  margin-bottom: var(--spacing-xs-8, 8px);
`;

const ModalDescription = styled(Body).attrs({ size: 'sm' })`
  color: var(--text-secondary, #535366);
  margin-bottom: var(--spacing-sm-16, 16px);
`;

const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
  margin-bottom: var(--spacing-sm-16, 16px);
`;

const OptionLabel = styled.label<{ isSelected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm-12, 12px);
  padding: var(--spacing-sm-12, 12px) var(--spacing-sm-16, 16px);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--border-radius-radius-md, 8px);
  cursor: pointer;
  transition: border-color 0.1s;

  &.isSelected {
    border-color: var(--brand-colors-primary, #0645b1);
  }

  input[type='radio'] {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border: 2px solid var(--stroke-medium, #b0b0be);
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 2px;
    position: relative;
    transition: border-color 0.1s;
  }

  &.isSelected input[type='radio'] {
    border-color: var(--brand-colors-primary, #0645b1);
    border-width: 5px;
  }
`;

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OptionTitle = styled(Body).attrs({ size: 'md', bold: true })``;

const OptionDesc = styled(Body).attrs({ size: 'sm' })`
  color: var(--text-secondary, #535366);
`;

const ReviewTable = styled.div`
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--border-radius-radius-md, 8px);
  overflow: hidden;
  margin-bottom: var(--spacing-sm-16, 16px);
`;

const ReviewRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm-16, 16px);
  padding: var(--spacing-sm-12, 12px) var(--spacing-sm-16, 16px);
  border-bottom: 1px solid var(--stroke-light, #dddde2);

  &:last-child {
    border-bottom: none;
  }
`;

const ReviewLabel = styled(Body).attrs({ size: 'sm' })`
  color: var(--text-secondary, #535366);
  flex-shrink: 0;
`;

const ReviewValue = styled(Body).attrs({ size: 'sm', bold: true })`
  text-align: right;
`;

const PremiumCallout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm-12, 12px);
  padding: var(--spacing-sm-12, 12px) var(--spacing-sm-16, 16px);
  background: var(--background-beige-light, #f9f8f6);
  border-radius: var(--border-radius-radius-md, 8px);
  margin-bottom: var(--spacing-sm-16, 16px);
`;

const CalloutIcon = styled.div`
  color: var(--brand-colors-primary, #0645b1);
  flex-shrink: 0;
  margin-top: 2px;
`;

const CalloutText = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-4, 4px);
`;

const CalloutTitle = styled(Heading).attrs({
  fontFamily: 'serif',
  size: 'sm',
})``;

const CalloutDesc = styled(Body).attrs({ size: 'sm' })`
  color: var(--text-secondary, #535366);
`;

// ─── Done step styles ─────────────────────────────────────────────────────────

const ExportFileCard = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm-24, 24px);
  padding: var(--spacing-xs-16, 16px) var(--spacing-sm-24, 24px);
  background: var(--background-beige-light, #f9f8f6);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--border-radius-radius-lg, 16px);
  overflow: hidden;
  width: 100%;
  margin-bottom: var(--spacing-sm-16, 16px);
`;

const ExportFileInfo = styled.div`
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ExportFileSubtext = styled(Body).attrs({ size: 'sm' })`
  color: var(--brand-colors-neutral-800, #6d6d7d);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BackgroundRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

// ─── Exporting step styles ────────────────────────────────────────────────────

const ExportingCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--spacing-xs-8, 8px) 0 var(--spacing-sm-16, 16px);
`;

const ProgressArc = styled.circle`
  transition: stroke-dashoffset 0.6s ease;
`;

const ProgressRingWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-sm-24, 24px);
`;

const ProgressRingCenter = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ProgressPct = styled(Mono).attrs({ size: 'lg' })`
  line-height: 1;
`;

const InProgressStepList = styled.div`
  border-top: 1px solid var(--stroke-light, #dddde2);
  width: 260px;
`;

const InProgressStepItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-12, 12px);
  height: 48px;
  padding: var(--spacing-xs-8, 8px) 0;
  border-bottom: 1px solid var(--stroke-light, #dddde2);
`;

const StepCheckWrap = styled.span<{ isDone?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--stroke-light, #dddde2);
  color: var(--brand-colors-neutral-600, #b1b1ba);

  &.isDone {
    background: var(--success-dark, #05b01c);
    border-color: var(--success-dark, #05b01c);
    color: var(--background-white, #ffffff);
  }
`;

const InProgressStepLabel = styled(Mono).attrs({ size: 'sm' })<{
  isDone?: boolean;
}>`
  text-transform: uppercase;
  color: var(--brand-colors-neutral-700, #91919e);

  &.isDone {
    color: var(--text-primary, #141413);
  }
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const RING_SIZE = 148;
const RING_STROKE = 10;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const IN_PROGRESS_STEPS = [
  'Querying records',
  'Enriching with metadata',
  'Formatting output',
  'Preparing and uploading',
];

const STEP_COMPLETE_DELAYS_MS = [400, 2500, 5000];
const POLLING_INTERVAL_MS = 3000;
const POLLING_TIMEOUT_MS = 120_000;

// ─── Types ────────────────────────────────────────────────────────────────────

type Format = 'csv' | 'json';
type Step = 'format' | 'review' | 'exporting' | 'done';

const STEPS: Step[] = ['format', 'review', 'exporting', 'done'];

interface ExportInfo {
  id: number;
  format: string;
  fileSize: number | null;
  recordCount: number;
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  apiPath: string;
  // Slug used to tag analytics events (e.g. 'mentions', 'readers',
  // 'profile_visitors'). Stays stable across UI copy changes.
  exportType: string;
  breadcrumbLabel: string;
  csvDescription: string;
  recordCount: number;
  recordNoun: string;
  calloutTitle: string;
  calloutDesc: string;
  resolveDownloadHref: (id: number) => string;
}

const FORMAT_LABELS: Record<Format, string> = {
  csv: 'CSV (spreadsheet)',
  json: 'JSON (raw data)',
};

// ─── Component ───────────────────────────────────────────────────────────────

const ExportFormatModal: React.FC<Props> = ({
  apiPath,
  exportType,
  breadcrumbLabel,
  csvDescription,
  recordCount,
  recordNoun,
  calloutTitle,
  calloutDesc,
  resolveDownloadHref,
}) => {
  const [open, setOpen] = useState(true);
  const [format, setFormat] = useState<Format>('csv');
  const [step, setStep] = useState<Step>('format');
  const [completedSteps, setCompletedSteps] = useState(0);
  const [progress, setProgress] = useState(0);
  const [exportError, setExportError] = useState(false);
  const [exportInfo, setExportInfo] = useState<ExportInfo | null>(null);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const doneRef = useRef(false);

  // Funnel tracking. `Started` (bare) + `Opened` (tagged) fire on mount;
  // per-step events fire on user-driven transitions; `Abandoned` fires on
  // close iff the user hasn't reached `done` (set via `completedRef`).
  const { fireEvent, completedRef } = useExportFunnelTracking({
    exportType,
    recordCount,
  });

  const stopAll = useCallback(() => {
    timeoutIdsRef.current.forEach(clearTimeout);
    timeoutIdsRef.current = [];
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('show_export_modal');
    window.history.replaceState({}, '', url.toString());
  }, []);

  useEffect(() => () => stopAll(), [stopAll]);

  const handleClose = useCallback(() => {
    stopAll();
    if (!completedRef.current) {
      fireEvent('abandoned', { last_step: step });
    }
    setOpen(false);
  }, [stopAll, fireEvent, step, completedRef]);

  const handleContinue = useCallback(() => {
    fireEvent('format_chosen', { format });
    setStep('review');
  }, [fireEvent, format]);

  const handleBack = useCallback(() => {
    setStep('format');
  }, []);

  const advanceProgress = useCallback((toStep: number) => {
    setProgress(Math.round((toStep / IN_PROGRESS_STEPS.length) * 100));
    setCompletedSteps(toStep);
  }, []);

  const handleStartExport = useCallback(() => {
    fireEvent('export_started', { format });
    setStep('exporting');
    doneRef.current = false;

    ApiUtils.post(apiPath, { export_format: format }).catch(() => {
      stopAll();
      fireEvent('errored', { phase: 'request' });
      setExportError(true);
    });

    timeoutIdsRef.current = STEP_COMPLETE_DELAYS_MS.map((delay, i) =>
      setTimeout(() => advanceProgress(i + 1), delay),
    );

    pollingRef.current = setInterval(() => {
      ApiUtils.get(apiPath)
        .then((data: { generating: boolean; latestExport?: ExportInfo }) => {
          if (!data.generating && data.latestExport && !doneRef.current) {
            doneRef.current = true;
            stopAll();
            advanceProgress(IN_PROGRESS_STEPS.length);
            setTimeout(() => {
              setExportInfo(data.latestExport!);
              completedRef.current = true;
              fireEvent('completed', {
                format,
                record_count: data.latestExport!.recordCount,
              });
              setStep('done');
            }, 1200);
          }
        })
        .catch(() => {});
    }, POLLING_INTERVAL_MS);

    timeoutIdsRef.current.push(
      setTimeout(() => {
        if (!doneRef.current) {
          stopAll();
          fireEvent('errored', { phase: 'timeout' });
          setExportError(true);
        }
      }, POLLING_TIMEOUT_MS),
    );
  }, [apiPath, format, advanceProgress, stopAll, fireEvent, completedRef]);

  const handleRetry = useCallback(() => {
    setExportError(false);
    setCompletedSteps(0);
    setProgress(0);
    setStep('review');
  }, []);

  const offset = RING_CIRCUMFERENCE * (1 - progress / 100);
  const recordsLabel = `${recordCount.toLocaleString()} ${recordNoun}${recordCount !== 1 ? 's' : ''}`;

  const callout = (
    <PremiumCallout>
      <CalloutIcon>
        <Icon name="auto_awesome" size={20} />
      </CalloutIcon>
      <CalloutText>
        <CalloutTitle>{calloutTitle}</CalloutTitle>
        <CalloutDesc>{calloutDesc}</CalloutDesc>
      </CalloutText>
    </PremiumCallout>
  );

  return (
    <Modal open={open} onClose={handleClose} containerStyle={{ maxWidth: 600 }}>
      <ModalContent>
        <Breadcrumb>
          <BreadcrumbText>Export</BreadcrumbText>
          <BreadcrumbSep>·</BreadcrumbSep>
          <BreadcrumbText>{breadcrumbLabel}</BreadcrumbText>
        </Breadcrumb>

        <ProgressTrack>
          {STEPS.map((s, i) => (
            <ProgressSegment key={s} filled={i <= STEPS.indexOf(step)} />
          ))}
        </ProgressTrack>

        {step === 'format' && (
          <>
            <ModalTitle>Choose a format</ModalTitle>
            <ModalDescription>
              Pick a file format. We will email you a secure download link when
              it is ready.
            </ModalDescription>

            <OptionsWrapper>
              <OptionLabel isSelected={format === 'csv'}>
                {/* eslint-disable-next-line no-restricted-syntax */}
                <input
                  type="radio"
                  name="export-format"
                  value="csv"
                  checked={format === 'csv'}
                  onChange={() => setFormat('csv')}
                />
                <OptionText>
                  <OptionTitle>CSV (spreadsheet)</OptionTitle>
                  <OptionDesc>{csvDescription}</OptionDesc>
                </OptionText>
              </OptionLabel>

              <OptionLabel isSelected={format === 'json'}>
                {/* eslint-disable-next-line no-restricted-syntax */}
                <input
                  type="radio"
                  name="export-format"
                  value="json"
                  checked={format === 'json'}
                  onChange={() => setFormat('json')}
                />
                <OptionText>
                  <OptionTitle>JSON (raw data)</OptionTitle>
                  <OptionDesc>
                    Structured data, ideal for further analysis.
                  </OptionDesc>
                </OptionText>
              </OptionLabel>
            </OptionsWrapper>

            {callout}

            <ModalButtonGroup>
              <TextButton onClick={handleClose}>Cancel</TextButton>
              <Button onClick={handleContinue} iconRight="arrow_forward">
                Continue
              </Button>
            </ModalButtonGroup>
          </>
        )}

        {step === 'review' && (
          <>
            <ModalTitle>Review your export</ModalTitle>
            <ModalDescription>Confirm what you are exporting.</ModalDescription>

            <ReviewTable>
              <ReviewRow>
                <ReviewLabel>Item</ReviewLabel>
                <ReviewValue>{breadcrumbLabel}</ReviewValue>
              </ReviewRow>
              <ReviewRow>
                <ReviewLabel>Records</ReviewLabel>
                <ReviewValue>{recordsLabel}</ReviewValue>
              </ReviewRow>
              <ReviewRow>
                <ReviewLabel>Format</ReviewLabel>
                <ReviewValue>{FORMAT_LABELS[format]}</ReviewValue>
              </ReviewRow>
              <ReviewRow>
                <ReviewLabel>Delivery</ReviewLabel>
                <ReviewValue>Email + in-browser download</ReviewValue>
              </ReviewRow>
            </ReviewTable>

            {callout}

            <ModalButtonGroup>
              <TextButton onClick={handleBack}>Back</TextButton>
              <Button onClick={handleStartExport}>Start export</Button>
            </ModalButtonGroup>
          </>
        )}

        {step === 'exporting' && (
          <>
            {exportError ? (
              <>
                <ModalTitle>Something went wrong</ModalTitle>
                <ModalDescription>
                  We couldn't start your export. Please try again.
                </ModalDescription>
                <ModalButtonGroup>
                  <TextButton onClick={handleClose}>Cancel</TextButton>
                  <Button onClick={handleRetry}>Try again</Button>
                </ModalButtonGroup>
              </>
            ) : (
              <>
                <ModalTitle>Preparing your export...</ModalTitle>
                <ExportingCenter>
                  <ProgressRingWrap
                    role="progressbar"
                    aria-valuenow={progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`Export progress: ${progress}%`}
                  >
                    <svg
                      width={RING_SIZE}
                      height={RING_SIZE}
                      viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                      aria-hidden="true"
                    >
                      <circle
                        cx={RING_SIZE / 2}
                        cy={RING_SIZE / 2}
                        r={RING_RADIUS}
                        fill="none"
                        stroke="var(--stroke-light, #dddde2)"
                        strokeWidth={RING_STROKE}
                      />
                      <ProgressArc
                        cx={RING_SIZE / 2}
                        cy={RING_SIZE / 2}
                        r={RING_RADIUS}
                        fill="none"
                        stroke="var(--brand-colors-primary, #0645b1)"
                        strokeWidth={RING_STROKE}
                        strokeLinecap="round"
                        strokeDasharray={RING_CIRCUMFERENCE}
                        strokeDashoffset={offset}
                        transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
                      />
                    </svg>
                    <ProgressRingCenter>
                      <ProgressPct aria-hidden="true">{progress}%</ProgressPct>
                    </ProgressRingCenter>
                  </ProgressRingWrap>

                  <InProgressStepList>
                    {IN_PROGRESS_STEPS.map((label, i) => {
                      const isDone = i < completedSteps;
                      return (
                        <InProgressStepItem key={label}>
                          <StepCheckWrap isDone={isDone}>
                            <Icon name="check" size={14} />
                          </StepCheckWrap>
                          <InProgressStepLabel isDone={isDone}>
                            {label}
                          </InProgressStepLabel>
                        </InProgressStepItem>
                      );
                    })}
                  </InProgressStepList>
                </ExportingCenter>
                <Divider size={8} />
                <BackgroundRow>
                  <TextButton inline onClick={handleClose}>
                    Run in the background
                  </TextButton>
                </BackgroundRow>
              </>
            )}
          </>
        )}
        {step === 'done' && exportInfo && (
          <>
            <ModalTitle>Your export is ready.</ModalTitle>
            <ModalDescription>
              We emailed you a secure link. Or download it directly.
            </ModalDescription>
            <ExportFileCard>
              <ExportFileInfo>
                <Body size="sm" bold>
                  Academia Premium – {breadcrumbLabel} (
                  {recordCount.toLocaleString()})
                </Body>
                <ExportFileSubtext>
                  {[
                    exportInfo.format.toUpperCase(),
                    formatFileSize(exportInfo.fileSize),
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </ExportFileSubtext>
              </ExportFileInfo>
              <ButtonLink
                href={resolveDownloadHref(exportInfo.id)}
                size="xs"
                icon="arrow_downward"
                download
              >
                Download
              </ButtonLink>
            </ExportFileCard>
            <Divider size={8} />
            <BackgroundRow>
              <TextButton onClick={handleClose}>Close</TextButton>
            </BackgroundRow>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ExportFormatModal;
